/**
 * Stickers and GIFs for the open workspace (StickersViewModel + the shared StickerCreatorViewModel), provided by the app
 * shell next to WorkspaceContext — only where the feature is on. Absent → composers show the plain emoji picker and
 * no sticker creation. Native: an `@Environment` value / `CompositionLocal` holding the same view models.
 */
import { MediaPickerViewModel, type MediaPickerState, type StickerCreatorState, type StickerCreatorViewModel, type StickersState, type StickersViewModel } from "@bundle/core";
import { createContext, useCallback, useContext, useSyncExternalStore } from "react";
import { useOwned } from "../hooks/owned.ts";

export type StickersContextValue = {
  stickers: StickersViewModel;
  creator: StickerCreatorViewModel;
};

export const StickersContext = createContext<StickersContextValue | null>(null);

const noopSubscribe = () => () => {};
const none = (): null => null;

/** The workspace's stickers and the creator (null when stickers are off here). */
export function useStickers(): { vm: StickersViewModel; state: StickersState; creator: StickerCreatorViewModel; creatorState: StickerCreatorState } | null {
  const ctx = useContext(StickersContext);
  const state = useSyncExternalStore(ctx?.stickers.subscribe ?? noopSubscribe, ctx?.stickers.getSnapshot ?? none, ctx?.stickers.getSnapshot ?? none);
  const creatorState = useSyncExternalStore(ctx?.creator.subscribe ?? noopSubscribe, ctx?.creator.getSnapshot ?? none, ctx?.creator.getSnapshot ?? none);
  if (ctx === null || state === null || creatorState === null) return null;
  return { vm: ctx.stickers, state, creator: ctx.creator, creatorState };
}

/** The composer picker of one conversation (or thread), shared by everything showing it; null when stickers are off. */
export function useMediaPicker(channelId: string, threadRootId: string | undefined): { vm: MediaPickerViewModel; state: MediaPickerState } | null {
  const ctx = useContext(StickersContext);
  const key = `picker:${ctx === null ? "off" : ctx.stickers.ws.wsId}:${channelId}:${threadRootId ?? ""}`;
  const create = useCallback((): PickerBox => {
    const vm = ctx === null ? null : new MediaPickerViewModel(ctx.stickers, threadRootId === undefined ? { channelId } : { channelId, threadRootId });
    return { vm, dispose: () => vm?.dispose() };
  }, [ctx, channelId, threadRootId]);
  const vm = useOwned(key, create).vm;
  const state = useSyncExternalStore(vm?.subscribe ?? noopSubscribe, vm?.getSnapshot ?? none, vm?.getSnapshot ?? none);
  return vm === null || state === null ? null : { vm, state };
}

type PickerBox = { vm: MediaPickerViewModel | null; dispose: () => void };
